import { MapPin, PackageCheck, Undo2 } from 'lucide-react';
import { Badge, Button, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import type { SupplyItem } from '../model/supply-items.js';

/**
 * QATORNING KO'RINISHI — holatga bog'liq hamma narsa bitta faylda.
 *
 * Nega bu `<tr>` qaytaradigan komponent EMAS: qatorni `Table` ning o'zi
 * chizadi va u bir ta'rifdan ikkita ko'rinish yasaydi — keng ekranda
 * jadval qatori, telefonda kartochka. Bu yerda ikkinchi `<tr>` yozilsa,
 * mobil ko'rinish undan bexabar qolardi va ikkalasi asta-sekin farq
 * qila boshlardi.
 *
 * Shuning uchun fayl qatorning teglarini emas, uning HOLATGA qarab
 * o'zgaradigan uch qismini beradi: sinflari, katakchasi va o'ng
 * chekkadagi holat ustuni. Uchalasi ham faqat `item.status` ni o'qiydi —
 * boshqa hech qanday manba yo'q.
 */

/**
 * Qator sinflari.
 *
 * `doc-row` — qat'iy 56px va 180ms lik fon o'tishi; `doc-row-placed` —
 * yashil tint va chap chekkadagi 2px chiziq. Ikkalasi ham `styles.css`
 * da, qatlamsiz CSS bo'lib turadi: ular `Table` ning O'Z utilitalarini
 * (`h-[var(--size-row)]`, `hover:bg-*`) qoplashi kerak va buni sinf
 * navbatiga tashlab bo'lmaydi.
 *
 * Qoldig'i nol satr YASHIL EMAS: yashil tint «bajarildi» degani va uni
 * hech kim bajarmagan. U oddiy qator bo'lib qoladi, holati esa o'ng
 * chekkadagi betaraf nishonda aytiladi.
 */
export const itemRowClassName = (item: SupplyItem): string =>
  cn('doc-row group/row', item.status === 'placed' ? 'doc-row-placed' : undefined);

export interface ItemCheckboxProps {
  item: SupplyItem;
  checked: boolean;
  onToggle: (id: string) => void;
}

/**
 * Belgilash katakchasi — HAR qatorda, bir xil joyda.
 *
 * Ilgari joylashtirilgan satrda katakcha umuman chizilmasdi va uning
 * o'rniga chiziq qo'yilardi: ustunlar shu satrda chapga surilib,
 * jadvalning vertikal tekisligi buzilardi. Endi katakcha qoladi —
 * bosilgan va o'chirilgan holatda, yashil rangda. Holat esa qatorning
 * chap chekkasidagi chiziq bilan aytiladi.
 *
 * Qoldig'i nol satrda katakcha O'CHIQ, lekin BO'SH: uni belgilab
 * bo'lmaydi (joylashtiradigan narsa yo'q), ammo bosilgan ko'rinishda
 * tursa «bajarildi» deb yolg'on aytgan bo'lardi.
 */
export const ItemCheckbox = ({ item, checked, onToggle }: ItemCheckboxProps) => {
  const done = item.status === 'placed';
  const empty = item.status === 'empty';
  return (
    <input
      type="checkbox"
      /* Yorliq — satr KODI: ekran o'qigich «uchinchi katakcha» emas,
         «ЦБ-00011748» deb o'qiydi. */
      aria-label={item.code}
      checked={!empty && (done || checked)}
      disabled={done || empty}
      onChange={() => onToggle(item.id)}
      onClick={(event) => event.stopPropagation()}
      className={cn(
        'size-4',
        empty
          ? 'cursor-default opacity-40'
          : done
            ? 'cursor-default accent-[var(--color-success)]'
            : 'cursor-pointer accent-[var(--color-accent)]',
      )}
    />
  );
};

export interface ItemStatusProps {
  item: SupplyItem;
  /** Savatda turibdimi — faqat shunda bekor qilish mumkin. */
  inBasket: boolean;
  onPlace: (item: SupplyItem) => void;
  onUndo: (id: string) => void;
}

/**
 * «HOLAT» ustuni — jadval ko'rinishi.
 *
 * Tugma GHOST: u 233 marta takrorlanadi va to'q ko'k bo'lganda ro'yxat
 * ko'k devorga aylanardi. Sahifadagi yagona to'ldirilgan tugma —
 * toolbar dagi «Yuklash».
 *
 * Har holatda BIR XIL qobiq: o'ngga tekislangan. Asosiy element (nishon
 * yoki tugma) doim ustunning o'ng chetida turadi.
 *
 * Bekor qilish tugmasi OQIMDAN TASHQARIDA (`absolute`, nishonning chap
 * yonida). Ilgari u oqimda edi va `opacity-0` bo'lsa ham 36px joy
 * egallardi — savatdagi satrlarning «Joylashtirildi» nishoni shuncha
 * chapga surilib, ustun tishli bo'lib qolardi.
 *
 * Qoldig'i nol satrda tugma ham, yashil nishon ham YO'Q: bosiladigan
 * joyi bo'lmagan tugma xatodek tuyuladi, yashil nishon esa qilinmagan
 * ishni bajarilgan deb ko'rsatadi. O'rnida betaraf nishon turadi va
 * SABABINI `title` da aytadi.
 */
export const ItemStatus = ({ item, inBasket, onPlace, onUndo }: ItemStatusProps) => (
  <div className="flex items-center justify-end">
    {item.status === 'empty' ? (
      <Badge tone="neutral" title={t('supply.lineEmptyHint')}>
        {t('supply.lineEmpty')}
      </Badge>
    ) : item.status === 'placed' ? (
      /* Langar — nishonning o'zi: qobiq butun katakni egallaydi va undan
         o'lchansa, tugma qo'shni ustunga tushib qolardi. */
      <span className="relative flex">
        <Badge tone="success">{t('supply.lineDone')}</Badge>

        {/* Bekor qilish faqat SAVATDAGI satrda: serverda allaqachon
            joylashtirilganini bu yerdan qaytarib bo'lmaydi. Sichqoncha
            qator ustiga kelganda ochiladi — doim ko'rinsa, nishon bilan
            teng vaznda turib qatorni chalkashtirardi. */}
        {inBasket ? (
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('supply.removeFromBasket')}
            title={t('supply.removeFromBasket')}
            onClick={() => onUndo(item.id)}
            /* 32px kvadrat: `size="sm"` ning `px-4` i bilan 48px edi va
               katakdan narx ustuniga chiqib ketardi. */
            className="absolute top-1/2 right-full mr-1 w-8 -translate-y-1/2 px-0! opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100"
          >
            <Undo2 size={14} aria-hidden />
          </Button>
        ) : null}
      </span>
    ) : (
      <Button variant="ghost" size="sm" onClick={() => onPlace(item)}>
        <PackageCheck size={14} aria-hidden />
        {t('supply.place')}
      </Button>
    )}
  </div>
);

/**
 * «QOLGANI» KATAGI — ikkita yalang'och son o'rniga bitta gap.
 *
 * Ilgari bu ustunda faqat bitta raqam turardi (`7`) va yonidagi
 * «Miqdor» ustunida ikkinchisi (`10`). Ikkalasining nisbati o'qilmasdi:
 * skladchi 10 tadan 3 tasini joylashtirgach jadvalda `10` va `7`
 * ko'rar, «3 tasi joylandimi?» degan savolga javobni o'zi hisoblab
 * chiqarishi kerak edi.
 *
 * Endi katak uch qavat va uchalasi ham BIR narsani aytadi:
 *   1. qolgan miqdor — qalin, chunki AMAL shundan kelib chiqadi;
 *   2. `3 / 10 joylandi` — bajarilgani, mayda va so'nik;
 *   3. ingichka chiziq — raqamni o'qimasdan ko'rinadigan ulush.
 *
 * Qoldig'i nol satrda katak BO'SH: u yerda na qolgani bor, na
 * joylashtirilgani — chiziq esa har doim nolda turib, xato tuyulardi.
 */
export const ItemPlacement = ({
  item,
  onShowLocation,
}: {
  item: SupplyItem;
  /**
   * «Qayerda turibdi» oynasini ochadi. Berilmasa ikkinchi qator oddiy
   * matn bo'lib qoladi — bosiladigan, lekin hech narsa qilmaydigan
   * matndan yomoni yo'q.
   */
  onShowLocation?: () => void;
}) => {
  if (item.status === 'empty') return <span className="text-text-faint">—</span>;

  const total = item.balanceQty;
  /* Bajarilgani AYIRMA bilan: savatdagi miqdor ham shunga kiradi va
     qatorning yashil holati bilan bir xil qoidaga bo'ysunadi. */
  const done = Math.round((total - item.remaining) * 1000) / 1000;
  const percent = total <= 0 ? 0 : Math.min(100, Math.round((done / total) * 100));

  return (
    <div className="flex w-full flex-col items-end gap-0.5">
      <span
        className={cn(
          'tabular-nums leading-tight',
          item.status === 'placed' ? 'text-text-muted' : 'font-semibold text-text',
        )}
      >
        {t('supply.qtyLeft', { qty: item.remaining })}
      </span>
      {/*
        BAJARILGANI — bosiladigan, chunki aynan shu son «qayerda?» degan
        savolni tug'diradi. Mahsulot javonga tushdi, lekin qaysi biriga
        tushgani hujjatda hech qayerda aytilmaydi: skladchi buni bilish
        uchun ombor chizmasini ochib, kataklarni birma-bir qarab
        chiqishi kerak edi.

        Hech narsa joylashtirilmagan satrda tugma YO'Q: bosilganda
        ko'rsatadigan joyi bo'lmasdi.
      */}
      {onShowLocation === undefined || done <= 0 ? (
        <span className="text-xs leading-tight tabular-nums text-text-muted">
          {t('supply.qtyPlacedOf', { done, total })}
        </span>
      ) : (
        <button
          type="button"
          onClick={onShowLocation}
          title={t('stock.locationOpen')}
          className={cn(
            'flex items-center gap-1 rounded-[var(--radius-sm)] px-1 -mx-1',
            'text-xs leading-tight tabular-nums text-accent underline-offset-2',
            'hover:underline focus-visible:outline-2 focus-visible:outline-offset-1',
            'focus-visible:outline-[var(--color-accent)]',
          )}
        >
          <MapPin size={11} aria-hidden className="shrink-0" />
          {t('supply.qtyPlacedOf', { done, total })}
        </button>
      )}
      {/* Chiziq bezak emas: ro'yxatni aylantirayotgan odam raqamlarni
          o'qimasdan qaysi satrda ish qolganini ko'radi. `aria-hidden` —
          ayni ma'lumot ustidagi ikki qatorda so'z bilan aytilgan. */}
      <div aria-hidden className="mt-0.5 h-[3px] w-full max-w-24 rounded-full bg-surface-2">
        <div
          className={cn(
            'h-full rounded-full transition-[width] duration-[var(--motion-slow)]',
            percent === 100 ? 'bg-success' : 'bg-accent',
          )}
          style={{ width: `${String(percent)}%` }}
        />
      </div>
    </div>
  );
};
